import { useEffect, useMemo, useRef, useState } from 'react'
import Axios from 'axios'
import recorder from '../../recorder/recorder'
import { Language, MessageType, Sender, Voice } from '../../globals'
import {
  generateStartTranscribe,
  generateStopTranscribe
} from '../../generateAudio'

type Props = {
  activeLanguage: Language | null
  talkToChat: (msg: string, s: Sender, t: MessageType) => void
  transcriptionUrl: string
  isBotBusy?: boolean
  stopSignal: boolean
  onStopped: () => void
  playAudio: (playUrl: string) => void
  voices?: Voice[]
  aivaIsTyping?: number
  isTranscribing: boolean
  onTranscriptionStatusChange: (busy: boolean) => void
  onRecordingStatusChange: (recording: boolean) => void
  conversationId: string
}

export default function AutoRecorderWav({
  activeLanguage,
  talkToChat,
  transcriptionUrl,
  stopSignal,
  onStopped,
  playAudio,
  voices,
  aivaIsTyping,
  onTranscriptionStatusChange,
  onRecordingStatusChange,
  isTranscribing,
  conversationId
}: Props) {
  // ---------- VAD tuning ----------
  const THR_ON_INIT = 0.5
  const THR_OFF_INIT = 0.5
  const THR_ON_REF = useRef(THR_ON_INIT)
  const THR_OFF_REF = useRef(THR_OFF_INIT)
  const MIN_FRAMES_TO_START = 4 // was 6 — make it a tad easier
  const MIN_UTTER_MS = 400
  const COOLDOWN_MS = 800
  const SILENCE_FRAMES = 90
  // --------------------------------

  const timer = useRef<NodeJS.Timeout>()
  const [running, setRunning] = useState(true)
  const [vu, setVu] = useState(0)
  const [recordingStart, setRecordingStart] = useState(0)

  const streamRef = useRef<MediaStream | null>(null)
  const ctxRef = useRef<AudioContext | null>(null) // NEW
  const anaRef = useRef<AnalyserNode | null>(null) // NEW
  const resumeGuards = useRef<(() => void)[] | null>(null)

  const silenceCounter = useRef(0)
  const voicedFrames = useRef(0)
  const utterStartMs = useRef<number | null>(null)
  const isRecording = useRef(false)
  const suppressRef = useRef(false) // NEW
  const rafId = useRef<number>()

  // Wrap playAudio with a mic cooldown (prevents TTS from re-triggering VAD)
  const safePlayAudio = useMemo(() => {
    return (url: string) => {
      try {
        playAudio(url)
      } finally {
        suppressRef.current = true
        window.setTimeout(() => {
          suppressRef.current = false
        }, COOLDOWN_MS)
      }
    }
  }, [playAudio])

  // start / stop whole session
  useEffect(() => {
    if (!running) return
    ;(async () => {
      // console.log(`Get user media ${streamRef?.current != null}`)
      // Better mic constraints
      streamRef.current = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: false,
          channelCount: 1
          // sampleRate: 16000 // uncomment if your backend prefers 16k
        }
      })
      await recorder.init(streamRef.current, () => {}) // we drive silence ourselves
      startVAD(streamRef.current!)
    })().catch((err) => {
      console.error('AutoRecorder startup failed', err)
      setRunning(false) // just flip the button state
      // Make sure flags are sane
      suppressRef.current = false
    })

    return stopAll
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running])

  useEffect(() => {
    if (stopSignal) stopAll()
  }, [stopSignal])

  // useEffect(() => () => clearInterval(timer.current), [])
  useEffect(() => () => resetState(), [conversationId])

  const stopSignalLatched = useRef(false)

  useEffect(() => {
    if (stopSignal && !stopSignalLatched.current) {
      stopSignalLatched.current = true
      stopAll()
    }
    if (!stopSignal) {
      stopSignalLatched.current = false
    }
  }, [stopSignal])

  const resetState = () => {
    // console.log('In reset state')
    
    // refs/flags
    suppressRef.current = false
    isRecording.current = false
    silenceCounter.current = 0
    voicedFrames.current = 0
    utterStartMs.current = null

    // thresholds → force re-calibration next start (tiny baseline)
    THR_ON_REF.current = THR_ON_INIT
    THR_OFF_REF.current = THR_OFF_INIT

    // UI bits
    setVu(0)

    // timers / listeners
    if (rafId.current) {
      cancelAnimationFrame(rafId.current)
      rafId.current = undefined
    }
    if (timer.current) {
      clearInterval(timer.current)
      timer.current = undefined
    }

    // gesture-resume listeners
    if (resumeGuards.current) {
      for (const off of resumeGuards.current) off()
      resumeGuards.current = null
    }
  }

  // VAD loop
  const startVAD = (stream: MediaStream) => {
    const ctx = new AudioContext()
    ctxRef.current = ctx
    const src = ctx.createMediaStreamSource(stream)
    suppressRef.current = false
    silenceCounter.current = 0
    voicedFrames.current = 0
    isRecording.current = false
    // 🔊 boost only the analysis path (does NOT change the recorded audio)
    const gain = ctx.createGain()
    gain.gain.value = 2.5 // 2–4 is a good start; tune as needed

    // (optional) light compressor to tame peaks; helps stability
    const comp = ctx.createDynamicsCompressor()
    comp.threshold.value = -32
    comp.knee.value = 24
    comp.ratio.value = 3
    comp.attack.value = 0.003
    comp.release.value = 0.25

    const ana = ctx.createAnalyser()
    ana.fftSize = 512
    ana.smoothingTimeConstant = 0.5
    // time-domain floats, so min/max dB on ana don't matter

    // wire: src -> gain -> comp -> ana
    src.connect(gain)
    gain.connect(comp)
    comp.connect(ana)

    anaRef.current = ana
    const data = new Float32Array(ana.fftSize) // ← floats, not bytes

    // --- ensure the context actually runs ---
    const tryResume = () => ctx.resume().catch(() => {})
    tryResume()
    // Fallback: resume on the next user gesture (once)
    const onUserGesture = () => tryResume()
    window.addEventListener('pointerdown', onUserGesture, {
      once: true,
      passive: true
    })
    window.addEventListener('keydown', onUserGesture, {
      once: true,
      passive: true
    })
    // Clean these up when we know the context is running
    const onStateChange = () => {
      if (ctx.state === 'running') {
        window.removeEventListener('pointerdown', onUserGesture)
        window.removeEventListener('keydown', onUserGesture)
        ctx.onstatechange = null
      }
    }
    ctx.onstatechange = onStateChange
    // keep a handle for stopAll() in case we exit early
    resumeGuards.current = [
      () => window.removeEventListener('pointerdown', onUserGesture),
      () => window.removeEventListener('keydown', onUserGesture),
      () => (ctx.onstatechange = null)
    ]

    // --- quick noise calibration (~300ms) ---
    const calFrames = 18
    let calSum = 0
    let calCount = 0

    const calib = (done: () => void) => {
      ana.getFloatTimeDomainData(data)
      let sum = 0
      for (let i = 0; i < data.length; i++) {
        const s = data[i]
        sum += s * s
      }
      const rms = Math.sqrt(sum / data.length)
      calSum += rms
      calCount++

      if (calCount >= calFrames) {
        const noise = calSum / calCount
        // ✅ thresholds for FLOAT RMS
        THR_ON_REF.current = Math.max(0.1, noise * 1.8 + 0.005)
        THR_OFF_REF.current = Math.max(0.08, noise * 1.4 + 0.003)

        // console.log('VAD ready:', {
        //   noise: noise.toFixed(4),
        //   thrOn: THR_ON_REF.current.toFixed(4),
        //   thrOff: THR_OFF_REF.current.toFixed(4),
        //   ctx: ctx.state
        // })
        done()
      } else {
        requestAnimationFrame(() => calib(done))
      }
    }

    let i = 0
    let rmsMax = 0
    const tick = () => {
      ana.getFloatTimeDomainData(data)
      let sum = 0
      for (let i = 0; i < data.length; i++) {
        const s = data[i]
        sum += s * s
      }
      const rms = Math.sqrt(sum / data.length)

      const db = 20 * Math.log10(rms + 1e-8)
      const norm = Math.min(1, Math.max(0, (db + 60) / 60))
      setVu(norm)

      // Only block STARTING when busy; if already recording, keep tracking to finish
      const blocked = suppressRef.current || aivaIsTyping
      if (blocked && !isRecording.current) {
        rafId.current = requestAnimationFrame(tick)
        return
      }

      const aboveOn = rms > THR_ON_REF.current
      const aboveOff = rms > THR_OFF_REF.current

      if (rms > rmsMax) {
        rmsMax = rms
      }
      if (i % 1000 == 0) {
        // console.log(
        //   `abonveOn ${aboveOn} -- rms ${rms} thr_on ${THR_ON_REF.current} -- rmsMax ${rmsMax}`
        // )
      }

      i++

      if (aboveOn || (isRecording.current && aboveOff)) {
        silenceCounter.current = 0
        voicedFrames.current += 1
        if (
          aboveOn &&
          !isRecording.current &&
          !isTranscribing &&
          !aivaIsTyping
        ) {
          if (voicedFrames.current >= MIN_FRAMES_TO_START) {
            setRecordingStart(Date.now())
            onRecordingStatusChange(true)
            // console.log(
            //   `Recording start: aboveOn ${aboveOn} -- rms ${rms} thr_on ${THR_ON_REF.current} -- rmsMax ${rmsMax}`
            // )
            recorder.start()
            utterStartMs.current = performance.now()
            isRecording.current = true
          }
        }
      } else if (isRecording.current) {
        silenceCounter.current += 1
        if (silenceCounter.current >= SILENCE_FRAMES) {
          const duration = Date.now() - recordingStart
          // console.log(
          //   `Recording stop: duration ${duration} aboveOff ${aboveOff} -- rms ${rms} thr_on ${THR_ON_REF.current} -- rmsMax ${rmsMax}`
          // )
          onRecordingStatusChange(false)
          finishUtterance()
        }
      } else {
        voicedFrames.current = 0
      }

      // Optional lightweight telemetry every ~5s
      // if ((performance.now() | 0) % 5000 < 16) // console.log({ rms, thr: THR_ON_REF.current })

      rafId.current = requestAnimationFrame(tick)
    }

    calib(() => tick())

    const noise = calSum / calCount // float RMS baseline
    // // set thresholds slightly above noise
    THR_ON_REF.current = Math.max(0.01, noise * 1.8 + 0.005)
    THR_OFF_REF.current = Math.max(0.008, noise * 1.4 + 0.003)

    // console.log('VAD ready:', {
    //   noise: (calSum / calCount).toFixed(2),
    //   thrOn: THR_ON_REF.current,
    //   thrOff: THR_OFF_REF.current,
    //   ctx: ctx.state
    // })
    return
  }

  // end one utterance, send to backend
  const finishUtterance = async () => {
    if (!isRecording.current) return

    isRecording.current = false
    const duration = utterStartMs.current
      ? performance.now() - utterStartMs.current
      : 0
    utterStartMs.current = null
    const wavBlob = recorder.stop()
    silenceCounter.current = 0
    voicedFrames.current = 0

    if (duration < MIN_UTTER_MS) return

    const startTranscribeUrl = generateStartTranscribe(
      activeLanguage?.locale || 'sv_SE',
      voices,
      0
    )
    if (startTranscribeUrl) safePlayAudio(startTranscribeUrl)
    onTranscriptionStatusChange(true)
    // suppressRef.current = true

    try {
      const { data } = await Axios.post(
        (activeLanguage as any)?.transcriptionUrl || transcriptionUrl,
        wavBlob,
        {
          headers: {
            'Content-Type': 'audio/wave',
            'X-System-Prompt': (activeLanguage as any)?.systemPrompt,
            'X-Language': activeLanguage?.locale?.split(/[_-]/)[0] || 'sv'
          }
        }
      )

      let text = (data ?? [])
        .map((d: any) => d.word)
        .join(' ')
        .trim()
      const short = text.split(/\s+/).length <= 1
      const commonGhosts = /^(ja|japp|mm|aha|tack|okej|ok)$/i
      if (short && commonGhosts.test(text)) text = ''

      if (text) {
        talkToChat(text, Sender.CUSTOMER, MessageType.TEXT)
      }

      const stopTranscribeUrl = generateStopTranscribe(
        activeLanguage?.locale || 'sv_SE',
        voices,
        0
      )
      if (stopTranscribeUrl) safePlayAudio(stopTranscribeUrl)

      const target = document.querySelector('#call-transcription #count')
      if (target) {
        let t = 3
        target.textContent = '3'
        clearInterval(timer.current)
        timer.current = setInterval(() => {
          t--
          if (t <= 0) clearInterval(timer.current)
          target.textContent = String(t)
        }, 1000)
      }
    } catch (err) {
      console.error('ASR failed', err)
    } finally {
      onTranscriptionStatusChange(false)
      setTimeout(() => {
        suppressRef.current = false
      }, COOLDOWN_MS)
    }
  }

  // teardown whole session
  const stopAll = () => {
    if (rafId.current) {
      cancelAnimationFrame(rafId.current)
      rafId.current = undefined
    }
    try {
      if (isRecording.current) recorder.stop()
    } catch {}
    try {
      streamRef.current?.getTracks().forEach((t) => t.stop())
    } catch {}
    streamRef.current = null
    try {
      ctxRef.current?.close()
    } catch {}
    ctxRef.current = null
    anaRef.current = null

    suppressRef.current = false
    isRecording.current = false
    silenceCounter.current = 0
    voicedFrames.current = 0
    utterStartMs.current = null
    setVu(0)
    setRunning(false)
    onStopped()
  }

  // UI
  return (
    <button
      className={`btn auto-recorder ${running ? 'on' : ''}`}
      title={running ? 'Stop auto-recorder' : 'Start auto-recorder'}
      onClick={async () => {
        if (running) {
          stopAll()
        } else {
          resetState()
          setRunning(true)
        }
      }}
      style={{ position: 'relative', width: 48 }}
      aria-busy={isTranscribing}
      aria-live="polite"
    >
      {running ? '■' : '▶'}
      {running && (
        <span
          aria-hidden
          style={{
            position: 'absolute',
            left: 0,
            bottom: -4,
            height: 3,
            width: `${Math.round(vu * 100)}%`,
            transition: 'width 50ms linear',
            background: 'lime'
          }}
        />
      )}
      {isTranscribing && (
        <span className="transcribing-dots" aria-label="Transcribing…">
          …
        </span>
      )}
      {!isTranscribing && !aivaIsTyping && (
        <span className="transcribing-dots" aria-label="Now you can talk..">
          {`()`}
        </span>
      )}
    </button>
  )
}
